/**
 * paywallTrigger4Suppression — anti-nag-loop cooldown for the WL-F1 "Kanzlei-
 * Übergabe" premium trigger (modules-loop-b.md WL-F1, Reviewer-Gate "kein
 * Dark-Pattern"). Persists a per-variant "last shown" timestamp in
 * `localStorage` so a dismissed export-moment modal / month-end banner does not
 * reappear on every render/reload within the cooldown window.
 *
 * Pure decision function + tiny localStorage side-effect, split so the decision
 * logic ({@link shouldShowKanzleiUebergabePaywall}) stays independently testable.
 * Best-effort: `localStorage` unavailability (private browsing, quota) never
 * blocks the trigger from showing — it just won't persist suppression for that
 * session (fail-open on storage errors, since this is a UX nicety, not a
 * security/legal control).
 *
 * @module utils/paywallTrigger4Suppression
 */

/** The two places Trigger 4 fires from (WL-F1) — each has its OWN cooldown so
 *  dismissing one does not suppress the other. */
export type PaywallTrigger4Variant = 'export_moment' | 'month_end';

/** Cooldown window: don't re-show the same variant within 7 days of a shown/dismissed cycle. */
export const PAYWALL_TRIGGER4_SUPPRESSION_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

function storageKey(variant: PaywallTrigger4Variant): string {
  return `faebill_paywall_kanzlei_uebergabe_${variant}_last_shown`;
}

/**
 * `true` if the variant may be shown now (never shown before, or the cooldown
 * window has elapsed since it was last marked shown). Fail-open: any storage
 * read error resolves to `true` (never silently block the trigger).
 *
 * @param variant - which Trigger-4 surface is asking.
 * @param now - "now" in epoch ms (injectable for tests).
 * @param windowMs - cooldown window (injectable for tests).
 */
export function shouldShowKanzleiUebergabePaywall(
  variant: PaywallTrigger4Variant,
  now: number = Date.now(),
  windowMs: number = PAYWALL_TRIGGER4_SUPPRESSION_WINDOW_MS,
): boolean {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return true;
    const raw = window.localStorage.getItem(storageKey(variant));
    if (!raw) return true;
    const last = Number(raw);
    if (!Number.isFinite(last)) return true;
    return now - last >= windowMs;
  } catch {
    return true;
  }
}

/**
 * Marks `variant` as shown "now" — starts a fresh cooldown window. Best-effort:
 * a storage write failure is silently ignored.
 *
 * @param variant - which Trigger-4 surface was shown.
 * @param now - "now" in epoch ms (injectable for tests).
 */
export function markKanzleiUebergabePaywallShown(
  variant: PaywallTrigger4Variant,
  now: number = Date.now(),
): void {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(storageKey(variant), String(now));
  } catch {
    // best-effort — ignore.
  }
}
